// -------------------------
// Declarative header schemas + (en|de)coders
// -------------------------
type ScalarType = "u8" | "u16" | "u32";

type LenFn = (ctx: Record<string, any>) => number;

// Typed schema helpers
type NumberField<Name extends string, Kind extends ScalarType> = {
  name: Name;
  type: Kind;
};

type BytesField<Name extends string> = {
  name: Name;
  type: "bytes";
  length: number | LenFn;
};

type SchemaFieldConst<Name extends string = string> =
  | NumberField<Name, ScalarType>
  | BytesField<Name>;

type FieldValue<F> = F extends { type: "bytes" } ? Uint8Array : number;

type StructFromSchema<S extends readonly SchemaFieldConst<string>[]> = {
  [F in S[number] as F["name"] & string]: FieldValue<F>;
};

export const defineSchema = <
  const S extends readonly SchemaFieldConst<string>[],
>(
  s: S
) => s;
export const defineSchemas = <
  const M extends Record<string, readonly SchemaFieldConst<string>[]>,
>(
  m: M
) => m;

export type SchemaStructs<
  M extends Record<string, readonly SchemaFieldConst<string>[]>,
> = {
  [K in keyof M]: StructFromSchema<M[K]>;
};

export function sizeofFieldSpec(
  f: SchemaFieldConst<string>,
  ctx: Record<string, any>
): number {
  if (f.type === "bytes")
    return typeof f.length === "function" ? (f.length as LenFn)(ctx) : f.length;
  return f.type === "u8" ? 1 : f.type === "u16" ? 2 : 4;
}

export function writeStruct<S extends readonly SchemaFieldConst<string>[]>(
  schema: S,
  values: StructFromSchema<S>
): Uint8Array {
  // compute total size (bytes fields may depend on previously set values)
  let total = 0;
  const ctx: Record<string, any> = { ...(values as any) };
  for (const f of schema) total += sizeofFieldSpec(f, ctx);

  const out = new Uint8Array(total);
  const view = new DataView(out.buffer);
  let o = 0;
  const vals = values as unknown as Record<string, any>;
  for (const f of schema) {
    if (f.type === "bytes") {
      const b: Uint8Array = vals[f.name];
      out.set(b, o);
      o += b.length;
      continue;
    }
    const n = Number(vals[f.name]) >>> 0;
    if (f.type === "u8") {
      view.setUint8(o, n);
      o += 1;
    } else if (f.type === "u16") {
      view.setUint16(o, n, true);
      o += 2;
    } else {
      view.setUint32(o, n, true);
      o += 4;
    }
  }
  return out;
}

export function readStruct<S extends readonly SchemaFieldConst<string>[]>(
  view: DataView,
  offset: number,
  schema: S
): { value: StructFromSchema<S>; offset: number } {
  const ctx: Record<string, any> = {};
  let o = offset;
  for (const f of schema) {
    if (f.type === "bytes") {
      const len =
        typeof f.length === "function" ? (f.length as LenFn)(ctx) : f.length;
      const u8 = new Uint8Array(view.buffer, view.byteOffset + o, len);
      ctx[f.name] = new Uint8Array(u8); // copy for safety
      o += len;
    } else if (f.type === "u8") {
      ctx[f.name] = view.getUint8(o);
      o += 1;
    } else if (f.type === "u16") {
      ctx[f.name] = view.getUint16(o, true);
      o += 2;
    } else {
      ctx[f.name] = view.getUint32(o, true);
      o += 4;
    }
  }
  return { value: ctx as StructFromSchema<S>, offset: o };
}
